// Help affordances used across pages. Two patterns:
//
//   <HelpHint>Long explanation…</HelpHint>
//      → a `?` icon that reveals a small popover on hover/focus.
//      Sits next to a section header or field label. When `href` is
//      set, the icon itself is a link to the deep Help page — click
//      it to navigate. Hover reveals the same explanation in a
//      popover with a redundant "Read more →" link.
//
//   <FieldCaption>Shown to participants at session start.</FieldCaption>
//      → a short sentence rendered under a field label, used to
//      answer "who reads this?" or "what is this for?".
//
// Both intentionally render in the proportional UI font; they should
// never look like code or like a debug surface.

import * as Tooltip from '@radix-ui/react-tooltip';
import { HelpCircle } from 'lucide-react';
import type { MouseEvent, ReactNode } from 'react';
import { cn } from './cn';

export interface HelpHintProps {
  /** Tooltip body. Plain text or short JSX (no headings). */
  children: ReactNode;
  /** Accessible label for the trigger button. */
  label?: string;
  /**
   * Optional deep link to the Help page. When set, the `?` icon is
   * itself an anchor — clicking navigates. The hover popover also
   * shows a "Read more →" link to the same target. The current page
   * hash is captured as `?from=…` on both so the Help page renders
   * a "← Back to {previous surface}" affordance.
   */
  href?: string;
  /** Override the trigger size/colour if the default needs adjusting. */
  className?: string;
}

const triggerClasses =
  'inline-flex size-5 shrink-0 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm transition-colors hover:border-primary/40 hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function HelpHint({ children, label = 'Help', href, className }: HelpHintProps) {
  function withFromSuffix(target: string): string {
    if (typeof window === 'undefined') return target;
    const here = window.location.hash;
    if (!here) return target;
    const sep = target.includes('?') ? '&' : '?';
    return `${target}${sep}from=${encodeURIComponent(here)}`;
  }

  // When `href` is set the trigger is a real link — click navigates.
  // Otherwise it's a button whose only job is to anchor the hover
  // popover; we swallow default click so Radix Tooltip's intentional
  // close-on-click doesn't dismiss before the user can read.
  const readMore = href ? withFromSuffix(href) : '';
  const trigger = href ? (
    <a href={readMore} aria-label={label} className={cn(triggerClasses, className)}>
      <HelpCircle className="size-3.5" />
    </a>
  ) : (
    <button
      type="button"
      aria-label={label}
      onClick={(e: MouseEvent<HTMLButtonElement>) => e.preventDefault()}
      className={cn(triggerClasses, className)}
    >
      <HelpCircle className="size-3.5" />
    </button>
  );

  return (
    <Tooltip.Provider delayDuration={150}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>{trigger}</Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side="top"
            align="center"
            sideOffset={6}
            className="z-50 max-w-xs rounded-md border border-border bg-card px-3 py-2 text-xs leading-relaxed text-card-foreground shadow-md"
          >
            {children}
            {href ? (
              <a
                href={readMore}
                className="mt-2 block text-xs font-medium text-primary hover:underline"
              >
                Read more →
              </a>
            ) : null}
            <Tooltip.Arrow className="fill-card" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}

export interface FieldCaptionProps {
  children: ReactNode;
  className?: string;
}

/**
 * Short caption rendered under a field label. Says who reads the
 * value, what format it takes, or anything else a user needs before
 * they edit. Always proportional font; never monospace.
 */
export function FieldCaption({ children, className }: FieldCaptionProps) {
  return (
    <p className={cn('mt-1 text-xs text-muted-foreground', className)}>{children}</p>
  );
}
